import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
    useLazyGetSeguimientoVerosimilitudQuery,
    useSaveSeguimientoVerosimilitudMutation,
    usePresentActividadSeguimientoVerosimilitudMutation
} from '../../../../services/visitasApi';
import SeguimientoVerosimilitudGrid, { SeguimientoRespuesta } from './SeguimientoVerosimilitudGrid';

interface RouteState {
    selectedVisita: any;
    idSeguimientoVisita: number;
    idVisitaOrigen: number | null;
    idVerosimilitud: number | null;
}

const buildSecciones = (respuestas: any[]) => {
    const map = new Map<number, { id: number; titulo: string; orden: number; preguntas: any[] }>();

    for (const r of respuestas) {
        const sec = r.seccion;
        if (!map.has(sec.id)) {
            map.set(sec.id, { id: sec.id, titulo: sec.titulo, orden: sec.orden ?? 0, preguntas: [] });
        }
        map.get(sec.id)!.preguntas.push({
            id: r.pregunta.id,
            texto: r.pregunta.texto,
            orden: r.pregunta.orden,
            // Número global de la pregunta, el mismo del PDF del seguimiento.
            numero: r.numeroPregunta ?? r.pregunta.orden
        });
    }

    return Array.from(map.values())
        .sort((a, b) => a.orden - b.orden)
        .map((s) => ({ ...s, preguntas: s.preguntas.sort((a: any, b: any) => a.orden - b.orden) }));
};

const SeguimientoVerosimilitudPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const { selectedVisita, idSeguimientoVisita } = (location.state ?? {}) as RouteState;

    // Determina si la actividad ya fue presentada (estado 2 = Realizado)
    const seguimientoActividad = selectedVisita?.actividades?.find((a: any) =>
        a.tipoVisita?.descripcion?.toUpperCase().includes('SEGUIMIENTO VEROSIMILITUD')
    );
    const isCompleted = seguimientoActividad?.estado === 2;

    const [allAnswered, setAllAnswered] = useState(false);
    const [respuestas, setRespuestas] = useState<SeguimientoRespuesta[]>([]);

    // El backend devuelve los ítems incumplidos de la verosimilitud de origen (RN-15) con lo
    // que ya haya respondido el preventor. No se pide el origen directo: ese endpoint exige ser
    // el preventor del origen y el seguimiento puede tener otro (SMP-352).
    const [fetchSeguimiento, { data: seguimientoData, isLoading, isError }] =
        useLazyGetSeguimientoVerosimilitudQuery();

    useEffect(() => {
        if (idSeguimientoVisita) {
            fetchSeguimiento({ idVisita: idSeguimientoVisita }, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [guardarSeguimiento, { isLoading: isSaving }] = useSaveSeguimientoVerosimilitudMutation();
    const [presentarSeguimiento, { isLoading: isPresenting }] =
        usePresentActividadSeguimientoVerosimilitudMutation();

    const isSubmitting = isSaving || isPresenting;

    const activeRespuestas: any[] = useMemo(
        () => seguimientoData?.respuestas ?? seguimientoData?.data?.respuestas ?? [],
        [seguimientoData]
    );

    const secciones = useMemo(() => buildSecciones(activeRespuestas), [activeRespuestas]);

    // Precarga lo ya respondido; los ítems sin respuesta quedan en blanco.
    const initialSeleccion = useMemo(
        () =>
            activeRespuestas.reduce<Record<number, 1 | 2 | null>>((acc, r) => {
                if (r.idTipoRespuesta === 1 || r.idTipoRespuesta === 2) {
                    acc[r.pregunta?.id] = r.idTipoRespuesta;
                }
                return acc;
            }, {}),
        [activeRespuestas]
    );

    const handleGridChange = useCallback(
        (nuevasRespuestas: SeguimientoRespuesta[], answered: boolean) => {
            setRespuestas(nuevasRespuestas);
            setAllAnswered(answered);
        },
        []
    );

    const handleSubmit = async () => {
        if (!seguimientoActividad?.id) {
            enqueueSnackbar('No se encontró la actividad de seguimiento.', { variant: 'error' });
            return;
        }

        try {
            await guardarSeguimiento({
                idVisita: idSeguimientoVisita,
                respuestas: respuestas.map(({ idPregunta, idTipoRespuesta }) => ({
                    idPregunta,
                    idTipoRespuesta
                }))
            }).unwrap();

            await presentarSeguimiento({
                idVisita: idSeguimientoVisita,
                idActividad: seguimientoActividad.id
            }).unwrap();

            enqueueSnackbar('Seguimiento enviado correctamente.', { variant: 'success' });
            navigate(-1);
        } catch {
            enqueueSnackbar('Error al enviar el seguimiento.', { variant: 'error' });
        }
    };

    const pendingBackendIds = !idSeguimientoVisita;

    return (
        <Box sx={{ pb: 10 }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 2, py: 3, backgroundColor: 'white', mb: 2 }}
            >
                <Typography variant="h3">
                    <strong>Seguimiento Verosimilitud</strong>
                </Typography>
                <Button variant="text" onClick={() => navigate(-1)}>
                    Volver
                </Button>
            </Box>

            {pendingBackendIds && (
                <Box sx={{ p: 4, backgroundColor: 'white' }}>
                    <Typography variant="body2" color="text.secondary">
                        Los datos de seguimiento aún no están disponibles (pendiente de backend).
                    </Typography>
                </Box>
            )}

            {!pendingBackendIds && isLoading && (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            )}

            {!pendingBackendIds && !isLoading && isError && (
                <Box sx={{ p: 4, backgroundColor: 'white' }}>
                    <Typography variant="body2" color="text.secondary">
                        El seguimiento de verosimilitud aún no está disponible para esta visita. Una vez
                        que el proceso de verosimilitud esté completo, podrá acceder al formulario de
                        seguimiento.
                    </Typography>
                </Box>
            )}

            {!pendingBackendIds && !isLoading && !isError && secciones.length === 0 && (
                <Box sx={{ p: 4, backgroundColor: 'white' }}>
                    <Typography variant="body2" color="text.secondary">
                        No hay items de seguimiento para esta visita.
                    </Typography>
                </Box>
            )}

            {!pendingBackendIds && !isLoading && secciones.length > 0 && (
                <Box sx={{ backgroundColor: 'white', p: 2 }}>
                    <SeguimientoVerosimilitudGrid
                        secciones={secciones}
                        initialSeleccion={initialSeleccion}
                        onChange={handleGridChange}
                    />
                </Box>
            )}

            {!pendingBackendIds && !isLoading && !isError && !isCompleted && secciones.length > 0 && !allAnswered && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2, px: 2, textAlign: 'right' }}
                >
                    Todos los ítems deben tener un estado marcado (Cumple o No Cumple) para enviar el
                    seguimiento.
                </Typography>
            )}

            {!pendingBackendIds && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, px: 2, gap: 2 }}>
                    <Button variant="text" onClick={() => navigate(-1)}>
                        Cancelar
                    </Button>
                    {!isError && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!allAnswered || isSubmitting || secciones.length === 0}
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? 'Enviando...' : 'Enviar seguimiento'}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SeguimientoVerosimilitudPage;
